import React from "react";
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  Chip,
} from "@mui/material";

import { fkId, sameId } from "../utils/coreHelpers";

export default function WardRoomBedMatrix({ wards = [], rooms = [], beds = [] }) {
  const roomsByWard = React.useMemo(() => {
    const map = new Map();
    for (const r of rooms) {
      const wid = fkId(r.ward || r.ward_id);
      if (!map.has(wid)) map.set(wid, []);
      map.get(wid).push(r);
    }
    return map;
  }, [rooms]);

  const bedsByRoom = React.useMemo(() => {
    const map = new Map();
    for (const b of beds) {
      const rid = fkId(b.room || b.room_id);
      if (!rid) continue;
      if (!map.has(rid)) map.set(rid, []);
      map.get(rid).push(b);
    }
    return map;
  }, [beds]);

  // Beds without room (optional): show under Ward as "No Room"
  const bedsByWardNoRoom = React.useMemo(() => {
    const map = new Map();
    for (const b of beds) {
      const rid = fkId(b.room || b.room_id);
      if (rid) continue; // has room
      const wid = fkId(b.ward || b.ward_id);
      if (!map.has(wid)) map.set(wid, []);
      map.get(wid).push(b);
    }
    return map;
  }, [beds]);

  return (
    <Paper sx={{ p: 2, borderRadius: 3 }}>
      <Typography sx={{ fontWeight: 1000, fontSize: 16, mb: 1 }}>
        Ward → Room → Bed Structure
      </Typography>

      <Table size="small">
        <TableHead>
          <TableRow sx={{ "& th": { fontWeight: 1000, fontSize: 12, color: "text.secondary" } }}>
            <TableCell sx={{ width: 260 }}>Ward Name</TableCell>
            <TableCell sx={{ width: 220 }}>Room</TableCell>
            <TableCell>Beds</TableCell>
          </TableRow>
        </TableHead>

        <TableBody>
          {wards.map((w) => {
            const wid = fkId(w.ward_id || w.id);
            const wardRooms = roomsByWard.get(wid) || [];
            const noRoomBeds = bedsByWardNoRoom.get(wid) || [];

            // If ward has no rooms AND no beds
            if (wardRooms.length === 0 && noRoomBeds.length === 0) {
              return (
                <TableRow key={wid}>
                  <TableCell
                    sx={{
                      fontWeight: 1000,
                      background: "rgba(2,32,53,0.03)",
                      borderRight: "1px solid rgba(2,32,53,0.08)",
                    }}
                  >
                    <Typography sx={{ fontWeight: 1000 }}>{w.name}</Typography>
                    <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                      Type: {w.ward_type || "-"} | Floor: {w.floor || "-"}
                    </Typography>
                  </TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>—</TableCell>
                  <TableCell sx={{ color: "text.secondary" }}>—</TableCell>
                </TableRow>
              );
            }

            // Build rows: each room is a row; plus an extra row for "No Room beds" if exists
            const rows = [...wardRooms];
            const includeNoRoomRow = noRoomBeds.length > 0;
            const totalRowsForWard = rows.length + (includeNoRoomRow ? 1 : 0);

            const wardCell = (
              <TableCell
                rowSpan={totalRowsForWard}
                sx={{
                  fontWeight: 1000,
                  verticalAlign: "top",
                  background: "rgba(2,32,53,0.03)",
                  borderRight: "1px solid rgba(2,32,53,0.08)",
                }}
              >
                <Typography sx={{ fontWeight: 1000 }}>{w.name}</Typography>
                <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                  Type: {w.ward_type || "-"} | Floor: {w.floor || "-"}
                </Typography>
              </TableCell>
            );

            const roomRows = rows.map((r, idx) => {
              const rid = fkId(r.room_id || r.id);
              const roomBeds = bedsByRoom.get(rid) || [];

              return (
                <TableRow key={`${wid}-${rid}`}>
                  {idx === 0 ? wardCell : null}

                  <TableCell sx={{ fontWeight: 800 }}>
                    Room {r.room_label}
                    {r.room_type ? (
                      <Typography sx={{ fontSize: 12, color: "text.secondary" }}>
                        {r.room_type}
                      </Typography>
                    ) : null}
                  </TableCell>

                  <TableCell>
                    {roomBeds.length ? (
                      <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                        {roomBeds.map((b) => (
                          <Chip
                            key={fkId(b.bed_id || b.id)}
                            label={b.bed_label}
                            size="small"
                            variant={b.is_active ? "filled" : "outlined"}
                          />
                        ))}
                      </Box>
                    ) : (
                      <Typography sx={{ color: "text.secondary" }}>No beds</Typography>
                    )}
                  </TableCell>
                </TableRow>
              );
            });

            const noRoomRow = includeNoRoomRow ? (
              <TableRow key={`${wid}-no-room`}>
                {rows.length === 0 ? wardCell : null}
                <TableCell sx={{ fontWeight: 800, color: "text.secondary" }}>
                  No Room
                </TableCell>
                <TableCell>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {noRoomBeds.map((b) => (
                      <Chip
                        key={fkId(b.bed_id || b.id)}
                        label={b.bed_label}
                        size="small"
                        variant={b.is_active ? "filled" : "outlined"}
                      />
                    ))}
                  </Box>
                </TableCell>
              </TableRow>
            ) : null;

            return (
              <React.Fragment key={wid}>
                {roomRows}
                {noRoomRow}
              </React.Fragment>
            );
          })}
        </TableBody>
      </Table>
    </Paper>
  );
}